import { z } from 'zod';

const DayString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** An airport wall clock without offset (`YYYY-MM-DDTHH:mm:ss`), never converted. */
const WallClock = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/);

/** One direction of the invite's flight, as the e-mail card shows it (no price). */
export const EventInviteFlightSchema = z.object({
  direction: z.enum(['OUTWARD', 'RETURN']),
  /** The first leg's airline and number. */
  carrier: z.string().min(1),
  flightNumber: z.string().min(1),
  fromIata: z.string().regex(/^[A-Z]{3}$/),
  toIata: z.string().regex(/^[A-Z]{3}$/),
  departLocal: WallClock,
  arriveLocal: WallClock,
  stops: z.number().int().nonnegative(),
  /** Door to door; absent when the supplier did not say (never computed from the wall clocks). */
  durationMinutes: z.number().int().positive().optional(),
});
export type EventInviteFlight = z.infer<typeof EventInviteFlightSchema>;

/** One card per direction of the trip. */
export const EventInviteFlightsSchema = z.array(EventInviteFlightSchema).min(1).max(2);

/**
 * `event_invite`: the traveler's invitation (or reminder) to confirm presence at a
 * "Grupos e eventos" event, or a notice about the flights of the invite (`flights_ready`,
 * `flights_refused`, and `purchases_open` when the organizer opens the purchases of a survey-mode
 * invite: no deadline semantics, the producer fills the fields from the invite). The mice service
 * publishes it as a system event; the
 * conversation localizes it (`_event_invite_vars`). Not strict: the conversation adds
 * `notificationDisplay` to the same metadata.
 */
export const EventInviteMetadataSchema = z.object({
  kind: z.enum(['invite', 'reminder', 'flights_ready', 'flights_refused', 'purchases_open']),
  eventId: z.string().min(1),
  eventName: z.string().min(1),
  /** First day of the event (`YYYY-MM-DD`). */
  startDate: DayString,
  /** Last day (`YYYY-MM-DD`); null for a one-day event. */
  endDate: DayString.nullable(),
  venueName: z.string().min(1).nullable(),
  /** Last day to confirm (`YYYY-MM-DD`). */
  deadline: DayString,
  travelerFirstName: z.string().min(1),
  companyName: z.string().min(1),
  organizerName: z.string().min(1),
  /** `flights_refused`: the flight(s) the traveler asked for, one per direction, e.g.
   *  "LA 3301 · 25/11 e LA 3300 · 27/11" (no price). Absent on any other kind and on a refusal
   *  recorded before the field: the copy falls back to the generic sentence. */
  requestedFlight: z.string().min(1).max(120).optional(),
  /** `flights_refused`: the same flight(s) as cards, one per direction. Absent on a refusal
   *  recorded before the field: the e-mail falls back to `requestedFlight`. */
  requestedFlights: EventInviteFlightsSchema.optional(),
  /** `flights_refused`: what the organizer wrote when refusing; absent when nothing was written. */
  refusalReason: z.string().min(1).max(500).optional(),
  /** `flights_ready`: the current recommendation, one per direction. */
  readyFlights: EventInviteFlightsSchema.optional(),
  /** The invite has no grid row: the producer pins the channel, e-mail only. */
  preferredChannel: z.literal('email'),
});
export type EventInviteMetadata = z.infer<typeof EventInviteMetadataSchema>;
